import { motion } from "framer-motion";
import { useState } from "react";
import { MicroLabel } from "@/components/decor/CropMarks";
import {
  Printer,
  Gift,
  Megaphone,
  Package,
  Layers,
  Monitor,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    n: "01",
    label: "Print Media",
    accent: "#0072CE",
    icon: Printer,
    body: "End-to-end print production with sharp finish and on-time delivery for collaterals, packaging and stationery systems.",
    items: ["Business Cards", "Brochures", "Catalogues", "Stationery"],
  },
  {
    n: "02",
    label: "Corporate Gifting",
    accent: "#D6007F",
    icon: Gift,
    body: "Curated gifting sets and hampers built around brand identity, occasion and recipient experience.",
    items: ["Gift Sets", "Hampers", "Office Merchandise", "Apparel"],
  },
  {
    n: "03",
    label: "Signage & Display",
    accent: "#FFD200",
    icon: Megaphone,
    body: "Indoor and outdoor signage systems engineered for visibility, durability, and brand consistency.",
    items: ["Acrylic Boards", "LED Signage", "Standees", "Retail Branding"],
  },
  {
    n: "04",
    label: "Promotional Products",
    accent: "#0072CE",
    icon: Package,
    body: "Branded utilities that travel — built for daily use, gifting, and high-touch brand impressions.",
    items: ["Bags", "Bottles", "Diaries", "Calendars"],
  },
  {
    n: "05",
    label: "Brand Development Support",
    accent: "#B89B5E",
    icon: Layers,
    body: "Logo application, stationery systems and packaging direction that turn identity into production-ready assets.",
    items: ["Identity Application", "Packaging Direction", "Collateral Systems"],
  },
  {
    n: "06",
    label: "Digital & Media Extension",
    accent: "#D6007F",
    icon: Monitor,
    body: "From print to platform — digital-ready assets prepared with the same precision as print sheets.",
    items: ["Digital Collaterals", "Social Templates", "Brand-Ready Files"],
  },
];

const ServiceCard = ({ s }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = s.icon;
  return (
    <motion.div
      data-testid={`service-card-${s.n}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative group p-7 md:p-8 cursor-default"
      style={{
        background: hovered ? "#141414" : "#0A0A0A",
        border: "1px solid rgba(192,192,192,0.15)",
        minHeight: 320,
        transition: "background 0.4s ease, border-color 0.4s ease",
        borderColor: hovered ? s.accent : "rgba(192,192,192,0.15)",
      }}
    >
      <span
        aria-hidden
        className="absolute top-0 left-0"
        style={{
          width: hovered ? 64 : 28,
          height: 3,
          background: s.accent,
          transition: "width 0.45s ease",
        }}
      />
      <div className="flex items-start justify-between">
        <span className="ko-overline" style={{ color: s.accent }}>
          {s.n} · Service
        </span>
        <Icon size={20} strokeWidth={1.5} color={hovered ? s.accent : "#C0C0C0"} />
      </div>

      <h3
        className="mt-8"
        style={{
          fontFamily: "Montserrat",
          fontWeight: 700,
          color: "#F5F1E8",
          fontSize: "clamp(20px, 2vw, 26px)",
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
        }}
      >
        {s.label}
      </h3>
      <p
        className="mt-4"
        style={{
          color: "#C0C0C0",
          fontSize: 13.5,
          lineHeight: 1.7,
        }}
      >
        {s.body}
      </p>

      <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5">
        {s.items.map((it) => (
          <li
            key={it}
            className="ko-overline"
            style={{ color: "#C0C0C0", fontSize: 10 }}
          >
            · {it}
          </li>
        ))}
      </ul>

      <a
        href="#contact"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
        }}
        data-testid={`service-inquire-${s.n}`}
        className="ko-overline mt-7 inline-flex items-center gap-2 transition-colors"
        style={{ color: hovered ? s.accent : "#F5F1E8" }}
      >
        Inquire
        <ArrowUpRight size={14} strokeWidth={1.5} />
      </a>
    </motion.div>
  );
};

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative"
      style={{ background: "#0A0A0A", paddingTop: 120, paddingBottom: 120 }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <MicroLabel index={5} accent="#B89B5E">
              Services
            </MicroLabel>
            <h2
              className="mt-6"
              style={{
                fontFamily: "Montserrat",
                fontWeight: 800,
                color: "#F5F1E8",
                fontSize: "clamp(34px, 5vw, 64px)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
                textTransform: "uppercase",
              }}
            >
              Built across
              <br />
              <span style={{ color: "#B89B5E" }}>six disciplines.</span>
            </h2>
          </div>
          <p
            style={{
              color: "#C0C0C0",
              maxWidth: 420,
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            Every service is engineered with the same production discipline — from
            small batch business cards to large-format signage installations.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "rgba(192,192,192,0.0)" }}>
          {SERVICES.map((s) => (
            <ServiceCard key={s.n} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
